import {
  findManagedTaskScreenLink,
  isManagedTaskScreenId,
  type IManagedTaskScreenLink,
  type IManagedTaskScreenProjectLike
} from "./managed-task-screen";
import {
  canvasCarriesWizardStamp,
  collectCreatedComponentNames,
  collectTargetActionIds,
  collectTargetKuikaIds,
  collectTargetParameterNames,
  findActionHostKuikaId,
  findWizardOwnedRegion,
  type OwnershipDictionary,
  type WizardOwnedRegion
} from "./managed-task-screen-ownership";

/**
 * KOA-18136 §40 — on a managed workflow task screen, a component the wizard
 * generated may have its STYLE changed and NOTHING ELSE.
 *
 * ## The law, in the user's words
 *
 *   "wizard elementlerinin herhangi bir şeyine kesinlikle dokunulmayacak. sadece
 *    stiline dokunulacak. action wiring'i, parametre binding'i falan
 *    değiştirilemez."
 *
 * No exception for "the platform re-imposes it on the next save anyway", and no
 * delete-then-recreate: both were reasoning of mine, not instructions.
 *
 * ## Why this file once said the opposite
 *
 * The gate shipped with exactly this law (KOA-17362). On 2026-08-11 I widened it
 * to "anything except deleting", reading the user's instruction not to cage the
 * refinement turn into a fixed template as licence to write INSIDE the wizard's
 * own components. That was an inference, it was never asked for, and the note at
 * the enforcement point (`mcp-tools/index.ts`) kept stating the real law while
 * the code no longer enforced it — so nothing flagged the drift. Live session
 * 889077ca is what it cost: generated decision buttons came back with their
 * ResumeProcess wiring rewritten, on several task screens.
 *
 * ## The rule as enforced
 *
 * On a managed task screen:
 *   - `ui_style` may address anything. Styling cannot remove or rewire.
 *   - READ operations may address anything (the deterministic repair passes must
 *     still be able to look at what is there).
 *   - every other operation is a WRITE: if its input addresses a kuikaId the
 *     wizard owns — the generated Form's subtree, the decision ButtonGroup's
 *     subtree, a stamped Header/Footer, or a container the Designer is adding
 *     INTO — it is refused.
 *   - AN ACTION IS PART OF ITS COMPONENT. A write that names an ACTION INSTANCE
 *     is resolved to the component hosting that action, and refused when that
 *     component is wizard-owned. The user's words: *"o bileşene bağlı action ve
 *     onun bindingleri de o bileşenin içinde, yani o bileşene dahil."* Asking
 *     only for component ids is what let the ResumeProcess parameters be emptied
 *     in live session 379e6653 — those writes carry `actionId`, not `kuikaId`,
 *     so a payload scan for components found nothing and passed them through.
 *     On a managed screen an action id whose host cannot be found is REFUSED, not
 *     assumed innocent: an unresolvable host is precisely the case that slipped.
 *   - a second decision `ButtonGroup` is refused by TYPE wherever it is created:
 *     the backend resolves the group by element type, first match wins, so a
 *     second one makes regeneration rebuild the wrong group and the real
 *     Approve/Reject buttons lose their wiring. The ownership walk cannot see it
 *     (it targets the screen root), so it is a separate check.
 *   - what the DESIGNER added to this screen is fully its own: add, style, move,
 *     bind, wire, delete — the law is about the wizard's components, not about
 *     the screen.
 *
 * Deletion keeps its own message, because it is the one change that cannot be
 * reverted at all: `UpdateButtonGroup` rebuilds the decision group by copying the
 * previous one, and with nothing to copy the ResumeProcess parameters come back
 * generic and the task can no longer be completed.
 *
 * ## Read-vs-write is decided by NAME, deliberately
 *
 * There is no registry of mutating operations in this codebase (the animation
 * zone map is incomplete by design), and a positive list of writes would drift
 * silently in the dangerous direction — a new write nobody added would pass. The
 * rule is therefore inverted: an operation is a read when its name begins with a
 * read verb, and everything else is treated as a write. A read misnamed as a
 * write is merely refused; a write cannot slip through by being forgotten.
 */

/**
 * Tool categories the rule does not apply to at all.
 *
 *   - `ui_style` — styling never removes anything.
 *   - `ui_workflow_dsl` — the DURABLE way to change a wizard component; it
 *     authors the process, not the screen tree.
 *   - `ui_screen_query` — read-only by construction.
 */
const EXEMPT_TOOLS: ReadonlySet<string> = new Set<string>(["ui_style", "ui_workflow_dsl", "ui_screen_query"]);

/**
 * Operations that REMOVE something — refused with their own message, because a
 * deletion is the one change to a generated component that cannot be undone.
 *
 * `delete` takes the component; `delete_from_event` / `delete_from_branch` take
 * an action off one (dropping `ResumeProcess` from the decision button is the
 * same unrecoverable outcome).
 */
const REMOVAL_OPS: ReadonlySet<string> = new Set<string>(["delete", "delete_from_event", "delete_from_branch"]);

/**
 * Verbs that begin a READ operation's name. See the module header for why the
 * rule is inverted rather than a list of writes.
 */
const READ_VERBS: readonly string[] = [
  "get",
  "list",
  "find",
  "query",
  "search",
  "describe",
  "has",
  "is",
  "check",
  "review",
  "preview",
  "validate",
  "read",
  "select"
];

/** True when this operation only LOOKS at the screen. */
export function isReadOperation(operation: string): boolean {
  const op: string = operation.trim().toLowerCase();
  if (op.length === 0) return false;
  return READ_VERBS.some((verb: string): boolean => op === verb || op.startsWith(`${verb}_`));
}

/**
 * The element type a second instance of which breaks the task — see the module
 * header. Refused by TYPE, wherever it is being created.
 */
const DECISION_GROUP_COMPONENT: string = "ButtonGroup";

/**
 * Everything this guard reads, RESOLVED by the caller.
 *
 * Deliberately not "the designer instance": `getSdInstance()` returns the raw
 * provider, whose flat state (`project`, `sdId`) is only reachable through
 * `getValues(sd)` — while `screenDesignDictionary` hangs off the instance's
 * `screenProvider`. Reading `sd.project` / `sd.sdId` straight off the instance
 * silently yields `undefined`, which made the whole guard a no-op that passed
 * every edit through (live 2026-07-27). Taking the resolved values makes that
 * mistake impossible here and keeps the module pure/testable.
 */
export interface IManagedGuardContext {
  /**
   * Flat-state project (`getValues(sd).project`).
   *
   * The full link shape, not just `screenId`: a refusal that names
   * `ui_workflow_dsl` is useless without the workflow and step it applies to, so
   * `processId` / `stepId` are read from the same array element.
   */
  project?: IManagedTaskScreenProjectLike;
  /** Flat-state current screen id (`getValues(sd).sdId`). */
  screenId?: string;
  /** `sd.screenProvider.state.screenDesignDictionary`. */
  dict?: OwnershipDictionary;
}

export interface ManagedTaskScreenRefusal {
  /** The kuikaId that triggered the refusal, or null when the canvas was unreadable. */
  readonly kuikaId: string | null;
  /** User/LLM facing message naming the durable alternative, when one exists. */
  readonly error: string;
}

/**
 * Refuse every WRITE that addresses a wizard-generated component, plus a second
 * decision ButtonGroup. Styling and reads pass; so does anything addressing only
 * what the Designer itself put on the screen.
 *
 * FAILS CLOSED on a managed screen whose canvas cannot be read: a write that
 * cannot be checked against ownership is exactly the one that must not be waved
 * through. Styling, reads and writes that address no component still pass. This
 * path is reached only by MCP tool calls (`McpToolExecutor.execute` — the AI
 * surface; the human editor mutates through the providers directly), so refusing
 * is never a locked editor. A missing `project` is not evidence of a managed
 * screen at all and passes.
 */
export function checkManagedTaskScreenOperation(
  ctx: IManagedGuardContext | null | undefined,
  toolName: string,
  operation: string,
  input: unknown
): ManagedTaskScreenRefusal | null {
  if (!ctx) return null;
  // KOA-18136 — EITHER source may prove it. The registry answers by identity and
  // is the stronger claim; the canvas stamp answers by evidence and survives the
  // registry losing the screen mid-turn, which is how live `3e95f572` wrote 57
  // renders onto two existing task screens with the guard silent. Additive on
  // purpose: neither source can UNSET managed-ness, and which component is owned
  // is still decided per target below.
  const registryKnows: boolean = isManagedTaskScreenId(ctx.project, ctx.screenId);
  if (!registryKnows && !canvasCarriesWizardStamp(ctx.dict)) return null;

  // Checked BEFORE the exempt list and before any ownership walk: this one is
  // about WHAT is being created, and its target is legitimately un-owned.
  const decisionGroup: ManagedTaskScreenRefusal | null = checkDecisionGroupInsertion(toolName, operation, input);
  if (decisionGroup !== null) return decisionGroup;

  if (EXEMPT_TOOLS.has(toolName)) return null;
  if (isReadOperation(operation)) return null;

  const targets: string[] = collectTargetKuikaIds(input);
  // An ACTION is part of the component it sits on, so a write that names one is
  // resolved to its host and judged there. Collected before the empty-target
  // shortcut below: a parameter write carries `actionId` and often no component
  // id at all, and that is exactly the write that emptied ResumeProcess.
  const actionIds: string[] = collectTargetActionIds(input);
  // A write can also be judged by the PARAMETER it names, with no component or action
  // id anywhere in the payload — so the "addresses nothing" shortcut has to know about
  // that shape too, or the backstop at the bottom is never reached.
  const touchesInteractionLink: boolean = collectTargetParameterNames(input).includes(INTERACTION_LINK_PARAMETER);
  if (targets.length === 0 && actionIds.length === 0 && !touchesInteractionLink) return null;

  if (ctx.dict === undefined) {
    return {
      kuikaId: null,
      error:
        `${toolName}.${operation} refused: this is a platform-managed workflow task screen, and the canvas tree ` +
        `could not be read to check whether the target is a wizard-generated component. On these screens a ` +
        `generated component may only be RESTYLED — its action wiring, its parameter bindings and its ` +
        `properties are the workflow engine's — so a write that cannot be verified is not allowed. ` +
        `Re-read the screen (ui_screen_query.get_tree) and retry, or add your own components instead.`
    };
  }

  for (const kuikaId of targets) {
    const region: WizardOwnedRegion | null = findWizardOwnedRegion(ctx.dict, kuikaId);
    if (region === null) continue;
    if (REMOVAL_OPS.has(operation)) {
      return { kuikaId, error: buildRemovalRefusal(ctx, toolName, operation, kuikaId, region) };
    }
    return { kuikaId, error: buildWriteRefusal(ctx, toolName, operation, kuikaId, region) };
  }

  for (const actionId of actionIds) {
    const host: string | null = findActionHostKuikaId(ctx.dict, actionId);
    if (host === null) {
      // Unresolvable host on a managed screen: refuse. This is the branch the
      // emptied ResumeProcess parameters would have hit — an action nobody can
      // place is not evidence that it belongs to the Designer.
      return { kuikaId: null, error: buildUnresolvedActionRefusal(ctx, toolName, operation, actionId) };
    }
    const region: WizardOwnedRegion | null = findWizardOwnedRegion(ctx.dict, host);
    if (region === null) continue;
    if (REMOVAL_OPS.has(operation)) {
      return { kuikaId: host, error: buildRemovalRefusal(ctx, toolName, operation, host, region) };
    }
    return { kuikaId: host, error: buildActionWriteRefusal(ctx, toolName, operation, host, region) };
  }

  // LAST, deliberately: when ownership settles the write it gives the better message
  // (it can name the host component). This is the backstop for the payload shapes
  // ownership cannot place — see the constant's note.
  return checkInteractionLinkWrite(toolName, operation, input);
}

/**
 * The ResumeProcess parameter that IS the link between a decision button and the
 * user interaction it completes.
 *
 * The wizard stamps it on every generated interaction button, and the platform reads
 * it back — never a friendly name, never a predefined id — to answer two separate
 * questions: which interaction this button completes, and (since KOA-15840) what that
 * interaction's button LOOKS like on a Process Automation client, whose entries are
 * emitted per active workflow version from this same lookup.
 *
 * So a write that lands on this parameter breaks two things at once, and neither is
 * repaired by the next regeneration: with the link gone the button completes nothing,
 * and the PA client falls back to a default-looking button. The ownership walk already
 * covers the normal shape of that write (it names the action, whose host is
 * wizard-owned). This check exists because the ownership walk answers "whose component
 * is this", and there are payload shapes that name the parameter without resolving to
 * a component at all. Here the parameter's NAME alone is enough to refuse — the cost of
 * a false positive is one refusal that names the durable alternative, and the cost of a
 * miss is a dead decision button.
 */
const INTERACTION_LINK_PARAMETER: string = "userinteractionid";

/**
 * Refuse a write that addresses the interaction-link parameter, whatever else it
 * addresses. Reads and styling never reach here (both return earlier).
 */
function checkInteractionLinkWrite(
  toolName: string,
  operation: string,
  input: unknown
): ManagedTaskScreenRefusal | null {
  const names: string[] = collectTargetParameterNames(input);
  if (!names.includes(INTERACTION_LINK_PARAMETER)) return null;
  return {
    kuikaId: null,
    error:
      `${toolName}.${operation} refused: this write addresses the "userInteractionId" parameter on a ` +
      `platform-managed workflow task screen. That parameter is the ONLY stable link between a decision ` +
      `button and the user interaction it completes — the platform resolves the interaction by it, and it ` +
      `also resolves the button's appearance on Process Automation clients from the same link, for every ` +
      `active workflow version. Overwriting it leaves a button that completes nothing and a PA client that ` +
      `renders a default one, and neither is repaired by the next process save. Change the interaction ` +
      `itself with ui_workflow_dsl (\`INTERACTION\`), which regenerates the button and its link together.`
  };
}

/**
 * Refuse a second decision ButtonGroup, wherever it is being created.
 *
 * Only creation matters: an existing group is covered by the deletion rule, and
 * restyling or moving one is now permitted like anything else.
 */
function checkDecisionGroupInsertion(
  toolName: string,
  operation: string,
  input: unknown
): ManagedTaskScreenRefusal | null {
  if (toolName !== "ui_component" || operation !== "add") return null;
  const created: string[] = collectCreatedComponentNames(input);
  if (!created.includes(DECISION_GROUP_COMPONENT)) return null;
  return {
    kuikaId: null,
    error:
      `ui_component.add refused: a second "${DECISION_GROUP_COMPONENT}" cannot be added to a platform-managed ` +
      `workflow task screen. The backend finds this screen's DECISION group by element type and takes the FIRST ` +
      `match in the tree, so a second one makes the next process save rebuild the wrong group — the real ` +
      `Approve/Reject buttons then lose their ResumeProcess wiring and the task can no longer be completed. ` +
      `Use plain Button components instead: they carry the same capability, look the same once styled, and are ` +
      `invisible to that lookup. Everything else you want to add to this screen is allowed.`
  };
}

/** Where the component sits, in the words the message uses. */
function describeRegion(region: WizardOwnedRegion): string {
  if (region === "form") return "inside the GENERATED form of this managed workflow task screen";
  if (region === "buttons") return "part of this managed task screen's generated DECISION BUTTON GROUP";
  return "part of the platform-generated chrome of this managed task screen";
}

/**
 * Why a deletion specifically cannot be undone here.
 *
 * Every region regenerates, but only a MISSING element makes regeneration lose
 * information: the form field comes back without its authored metadata link, and
 * the decision group comes back with generic ResumeProcess parameters because
 * `UpdateButtonGroup` had no previous group to copy events from.
 */
function describeRemovalConsequence(region: WizardOwnedRegion): string {
  if (region === "buttons") {
    return (
      "the backend rebuilds that button group on every UpdateWorkflowScreens by copying the OLD group's events " +
      "and repairing its ResumeProcess parameters — delete it and there is nothing to copy, so the task's " +
      "Approve/Reject can no longer complete the step"
    );
  }
  if (region === "form") {
    return (
      "the backend rebuilds that form from the workflow's form metadata, and a field removed from the tree is " +
      "regenerated without what the screen carried for it"
    );
  }
  return "the wizard generated and stamped it, and the workflow engine re-places it on every UpdateWorkflowScreens";
}

/** How to reach the DSL from here — useless advice without the workflow id. */
function describeDslPrecondition(ctx: IManagedGuardContext): string {
  const link: IManagedTaskScreenLink | null = findManagedTaskScreenLink(ctx.project, ctx.screenId);
  if (link?.processId === undefined) return "";
  return (
    `\nThe DSL needs its canvas first: ui_project_extended.navigate_to_workflow { workflowId: "${link.processId}" }` +
    (link.stepId !== undefined ? `, then address step "${link.stepId}".` : ".")
  );
}

/**
 * The refusal text for a WRITE that is not a deletion.
 *
 * Three things have to be in it or the caller retries: the law (only style), the
 * durable route for changing what the generator produces, and where its own work
 * belongs — the last one because a bare refusal on a screen the turn was sent to
 * improve reads as a total lockout (the KOA-17362 retry storm).
 */
function buildWriteRefusal(
  ctx: IManagedGuardContext,
  toolName: string,
  operation: string,
  kuikaId: string,
  region: WizardOwnedRegion
): string {
  const link: IManagedTaskScreenLink | null = findManagedTaskScreenLink(ctx.project, ctx.screenId);
  const step: string = link?.stepId ?? "<step>";
  const durable: string =
    region === "buttons"
      ? `INTERACTION "<label>" SET <outputParam>=<value>   (the decision buttons come from the step's user interactions)`
      : `SCREEN_FIELD ${step} "<boParam>" <hidden|readonly|label "...">   (changes what the generator emits)`;

  return (
    `${toolName}.${operation} refused on kuikaId ${kuikaId}: it is ${describeRegion(region)}. On a managed task ` +
    `screen only its STYLE may be changed — its action wiring, its parameter bindings, its properties, its ` +
    `position and its existence belong to the workflow engine, which re-generates them from the process. ` +
    `Rewriting them here breaks the running task (a decision button whose ResumeProcess parameters were ` +
    `overwritten can no longer complete the step) and is reverted or corrupted on the next process save.` +
    `\nIf the CONTENT must change, author it in the workflow DSL, which changes what the generator produces:` +
    `\n  ${durable}${describeDslPrecondition(ctx)}` +
    `\nTo build something new here, add your own components (at the screen root or inside a container you ` +
    `created) and wire THOSE — they are entirely yours. Styling anything on this screen is always allowed.`
  );
}

/**
 * The refusal text for a write that reached a wizard component THROUGH its action.
 *
 * Separate wording from the component write because the caller will not otherwise
 * understand what it hit: it named an action instance, not a component, and the
 * answer has to say which component that action belongs to and why that settles
 * it. The rule is the user's: an action attached to a component, and that action's
 * bindings, are part of that component.
 */
function buildActionWriteRefusal(
  ctx: IManagedGuardContext,
  toolName: string,
  operation: string,
  hostKuikaId: string,
  region: WizardOwnedRegion
): string {
  return (
    `${toolName}.${operation} refused: the action it addresses sits on kuikaId ${hostKuikaId}, which is ` +
    `${describeRegion(region)}. An action attached to a wizard-generated component — and that action's ` +
    `parameter bindings — is PART of that component, so only its STYLE may be changed. Writing its ` +
    `parameters breaks the running task: a decision button whose ResumeProcess parameters were overwritten ` +
    `can no longer complete the step.` +
    `\nWhat the decision buttons do comes from the step's user interactions, authored in the workflow DSL:` +
    `\n  INTERACTION "<label>" SET <outputParam>=<value>${describeDslPrecondition(ctx)}` +
    `\nActions on components YOU added to this screen are entirely yours — attach, bind and rewire those freely.`
  );
}

/**
 * The refusal text for an action whose host component cannot be found.
 *
 * Fail-closed by design: on a managed task screen an action nobody can place is
 * not evidence that it belongs to the Designer. This is the branch the emptied
 * ResumeProcess parameters of live session 379e6653 would have hit — those writes
 * carried an action id and no component id, and the gate had nothing to resolve.
 */
function buildUnresolvedActionRefusal(
  ctx: IManagedGuardContext,
  toolName: string,
  operation: string,
  actionId: string
): string {
  return (
    `${toolName}.${operation} refused: it addresses action instance ${actionId}, and no component on this ` +
    `managed task screen carries that action — so it cannot be checked against the wizard's ownership. On ` +
    `these screens a generated component's action wiring and parameter bindings are the workflow engine's, ` +
    `and a write that cannot be verified is not allowed.` +
    `\nRe-read the screen (ui_screen_query.list_current_actions on the component you mean, or ` +
    `ui_action.list_screen_actions) and address the action by the component that hosts it, or add your own ` +
    `component and wire that instead. Styling anything on this screen is always allowed.`
  );
}

/**
 * The refusal text for a deletion.
 *
 * It names the ONE durable route that can legitimately remove a generated
 * element — the workflow DSL, which changes what the generator produces instead
 * of deleting its output — and it states plainly that everything else on this
 * screen is permitted, so the caller does not read this as a general lockout and
 * retry the same patch twenty times (the KOA-17362 live failure).
 */
function buildRemovalRefusal(
  ctx: IManagedGuardContext,
  toolName: string,
  operation: string,
  kuikaId: string,
  region: WizardOwnedRegion
): string {
  const link: IManagedTaskScreenLink | null = findManagedTaskScreenLink(ctx.project, ctx.screenId);
  const step: string = link?.stepId ?? "<step>";
  const durable: string =
    region === "buttons"
      ? `INTERACTION "<label>" SET <outputParam>=<value>   (the decision buttons come from the step's user interactions)`
      : `SCREEN_FIELD ${step} "<boParam>" hidden   (hides a generated field for good, instead of deleting it)`;

  return (
    `${toolName}.${operation} refused on kuikaId ${kuikaId}: it is ${describeRegion(region)}, and ` +
    `${describeRemovalConsequence(region)}. Deleting is the ONE change to a wizard-generated component that ` +
    `cannot be undone.\nIf it should not be there, author that in the workflow DSL, which changes what the ` +
    `generator produces:\n  ${durable}${describeDslPrecondition(ctx)}` +
    `\nOn this screen a generated component may only be RESTYLED. To build something new, add your own ` +
    `components and wire THOSE — they are entirely yours.`
  );
}
